import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Loader2 } from 'lucide-react';

/**
 * Gates a route behind authentication. Optionally enforces a role
 * (or list of roles) and/or a module-level permission.
 */
export default function ProtectedRoute({ children, role, requirePermission }) {
  const { user, hasPermission } = useAuth();

  // Still checking session
  if (user === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  // Not authenticated → redirect to login
  if (user === false) {
    return <Navigate to="/login" replace />;
  }

  // Role check
  if (role) {
    const allowed = Array.isArray(role) ? role : [role];
    if (!allowed.includes(user.role)) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
          <div className="bg-white p-8 rounded-2xl shadow border border-slate-200 max-w-md text-center">
            <h2 className="text-xl font-bold text-slate-900 mb-2">Accès refusé</h2>
            <p className="text-slate-500 text-sm">Cette section est réservée aux utilisateurs avec le rôle : <span className="font-semibold">{allowed.join(', ')}</span>.</p>
          </div>
        </div>
      );
    }
  }

  // Permission check
  if (requirePermission) {
    const { module, action = 'read' } = requirePermission;
    if (!hasPermission(module, action)) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
          <div className="bg-white p-8 rounded-2xl shadow border border-slate-200 max-w-md text-center">
            <h2 className="text-xl font-bold text-slate-900 mb-2">Permission insuffisante</h2>
            <p className="text-slate-500 text-sm">Vous n'avez pas les droits <span className="font-semibold">{action}</span> sur le module <span className="font-semibold">{module}</span>.</p>
          </div>
        </div>
      );
    }
  }

  return children;
}
